import { type FC, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { enqueueSnackbar } from "notistack";
import { TOAST } from "@/shared/constants/toastMessages";

import { PeopleSelector } from "@/shared/components/PeopleSelector";
import { IconButton } from "@/shared/components/ui/icon-button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Relationship } from "@/shared/constants/relationshipOptions";
import { useUserContacts } from "@/shared/hooks/useUserContacts";
import { supabase } from "@/shared/lib/supabase";
import {
  MARITAL_STATUS_LABELS,
  getMaritalStatus,
  toMaritalStatus,
  withMaritalStatus,
  type MaritalStatus,
} from "@/shared/utils/maritalStatus";
import { contactFullName } from "@/shared/utils/contactUtils";
import { sentryUtils } from "@/shared/utils/sentry";
import {
  EarlierMarriagesFields,
  createEmptyMarriage,
} from "@/pages/postloss/probate/components/family-triage/EarlierMarriagesFields";
import { WHY_WE_ASK } from "@/shared/constants/whyWeAsk";

import type { PersonContact } from "@/shared/types/estateContact";
import type { EarlierMarriageRow } from "@/shared/types/probateTypes";

import { SummaryCard, SummaryRow } from "./SummaryCard";

import { fetchEstateJsonFields } from "../hooks/useEstateSummaryRecords";

import type { EstateRow, MarriageHistoryRow } from "../hooks/useEstateSummaryRecords";
import type { SectionEditProps } from "./sectionEdit";

const SPOUSE_RELATIONSHIPS = new Set<string>([Relationship.Spouse, Relationship.Partner]);
const CHILD_RELATIONSHIPS = new Set<string>([Relationship.Child, Relationship.StepChild]);
const SHOWS_SPOUSE = new Set<MaritalStatus>(["married", "domestic_partnership"]);

type PriorMarriages = "yes" | "no" | "";

interface FamilyForm {
  maritalStatus: MaritalStatus | "";
  spouseId: number | null;
  childIds: (number | null)[];
  priorMarriages: PriorMarriages;
  marriages: EarlierMarriageRow[];
}

function priorMarriagesFlag(marriageInfo: EstateRow["marriage_info"]): boolean | undefined {
  if (!marriageInfo || typeof marriageInfo !== "object" || Array.isArray(marriageInfo)) {
    return undefined;
  }
  const flag = marriageInfo.prior_marriages;
  return typeof flag === "boolean" ? flag : undefined;
}

function toMarriageRow(history: MarriageHistoryRow): EarlierMarriageRow {
  const parts = history.spouse_name.trim().split(/\s+/);
  return {
    id: String(history.id),
    firstName: parts[0] ?? "",
    middleName: parts.length > 2 ? parts.slice(1, -1).join(" ") : "",
    lastName: parts.length > 1 ? parts[parts.length - 1] : "",
    endYear: history.end_year ? String(history.end_year) : "",
    endReason: (history.end_reason as EarlierMarriageRow["endReason"]) ?? "",
  };
}

function marriageErrors(form: FamilyForm): Record<string, string> {
  if (form.priorMarriages !== "yes") return {};
  const errors: Record<string, string> = {};
  form.marriages.forEach((marriage, i) => {
    if (!marriage.firstName.trim()) errors[`marriage_${i}_first_name`] = "Enter a first name";
    if (!marriage.lastName.trim()) errors[`marriage_${i}_last_name`] = "Enter a last name";
  });
  return errors;
}

interface FamilySectionProps extends SectionEditProps {
  estate: EstateRow;
  marriageHistory: MarriageHistoryRow[];
  onSaved: () => void;
}

export const FamilySection: FC<FamilySectionProps> = ({
  estate,
  marriageHistory,
  onSaved,
  isEditing,
  canEdit,
  onEdit,
  onClose,
}) => {
  const { contacts, updateContactAsync } = useUserContacts();
  const [form, setForm] = useState<FamilyForm | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  const maritalStatus = getMaritalStatus(estate);
  const spouse = contacts?.find((c) => c.relationship && SPOUSE_RELATIONSHIPS.has(c.relationship));
  const children = (contacts ?? []).filter(
    (c) => c.relationship && CHILD_RELATIONSHIPS.has(c.relationship),
  );
  const priorFlag = priorMarriagesFlag(estate.marriage_info);
  const priorMarriages: PriorMarriages =
    marriageHistory.length > 0 || priorFlag === true ? "yes" : priorFlag === false ? "no" : "";

  const update = (changes: Partial<FamilyForm>) =>
    setForm((prev) => (prev ? { ...prev, ...changes } : prev));

  const handleEdit = () => {
    setForm({
      maritalStatus: maritalStatus ?? "",
      spouseId: spouse?.id ?? null,
      childIds: children.map((c) => c.id),
      priorMarriages,
      marriages: marriageHistory.map(toMarriageRow),
    });
    setErrors({});
    onEdit();
  };

  const setRelationship = (id: number, relationship: string | null) =>
    updateContactAsync({ id, relationship });

  const saveSpouse = async (draft: FamilyForm) => {
    if (!draft.maritalStatus) return;
    if (!SHOWS_SPOUSE.has(draft.maritalStatus)) {
      if (spouse) await setRelationship(spouse.id, Relationship.FormerSpouse);
      return;
    }
    if (draft.spouseId === (spouse?.id ?? null)) return;
    if (spouse) await setRelationship(spouse.id, null);
    if (draft.spouseId) {
      await setRelationship(
        draft.spouseId,
        draft.maritalStatus === "domestic_partnership" ? Relationship.Partner : Relationship.Spouse,
      );
    }
  };

  const saveChildren = async (draft: FamilyForm) => {
    const nextIds = new Set(draft.childIds.filter((id): id is number => id !== null));
    const removed = children.filter((c) => !nextIds.has(c.id));
    const added = [...nextIds].filter((id) => !children.some((c) => c.id === id));
    for (const child of removed) await setRelationship(child.id, null);
    for (const id of added) await setRelationship(id, Relationship.Child);
  };

  const saveMarriageHistory = async (draft: FamilyForm) => {
    const rows =
      draft.priorMarriages === "yes"
        ? draft.marriages.map((m) => ({
            estate_id: estate.estate_id,
            spouse_name: [m.firstName, m.middleName, m.lastName]
              .map((part) => part.trim())
              .filter(Boolean)
              .join(" "),
            end_year: m.endYear ? Number(m.endYear) : null,
            end_reason: m.endReason || null,
          }))
        : [];
    const { data: inserted, error: insertError } = rows.length
      ? await supabase.from("estate_marriage_history").insert(rows).select("id")
      : { data: [], error: null };
    if (insertError) throw insertError;

    const keptIds = (inserted ?? []).map((row) => row.id);
    let staleRows = supabase
      .from("estate_marriage_history")
      .delete()
      .eq("estate_id", estate.estate_id);
    if (keptIds.length > 0) staleRows = staleRows.not("id", "in", `(${keptIds.join(",")})`);
    const { error } = await staleRows;
    if (error) throw error;
  };

  const handleDone = async () => {
    if (!form) return;
    const nextErrors = marriageErrors(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setIsSaving(true);
    try {
      const current = await fetchEstateJsonFields(estate.estate_id);
      const metadata = (current.estate_metadata ?? {}) as Record<string, unknown>;
      const marriageInfo = withMaritalStatus(
        current.marriage_info,
        toMaritalStatus(form.maritalStatus),
      );
      const { error } = await supabase
        .from("estates")
        .update({
          marriage_info: {
            ...(marriageInfo as Record<string, unknown>),
            prior_marriages: form.priorMarriages ? form.priorMarriages === "yes" : null,
          },
          estate_metadata: { ...metadata, partnership: null },
        })
        .eq("estate_id", estate.estate_id);
      if (error) throw error;

      await saveSpouse(form);
      await saveChildren(form);
      await saveMarriageHistory(form);

      enqueueSnackbar(TOAST.CHANGES_SAVED, { variant: "success" });
      onClose();
    } catch (err) {
      enqueueSnackbar(TOAST.GENERIC_ERROR, { variant: "error" });
      sentryUtils.captureError(err, "Summary family save failed", { estateId: estate.estate_id });
    } finally {
      onSaved();
      setIsSaving(false);
    }
  };

  const takenIds = (current: number | null) => (contact: PersonContact) =>
    contact.id === current || !form?.childIds.includes(contact.id);

  const editingSpouse = form?.maritalStatus && SHOWS_SPOUSE.has(form.maritalStatus);

  return (
    <SummaryCard
      title="Family"
      edit={{
        isEditing,
        canEdit,
        isSaving,
        onEdit: handleEdit,
        onCancel: onClose,
        onDone: handleDone,
      }}
    >
      <SummaryRow label="Marital status" tooltip={WHY_WE_ASK.maritalStatus}>
        {isEditing && form ? (
          <Select
            value={form.maritalStatus}
            onValueChange={(value) => update({ maritalStatus: value as MaritalStatus })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(MARITAL_STATUS_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          maritalStatus && MARITAL_STATUS_LABELS[maritalStatus]
        )}
      </SummaryRow>
      {(isEditing ? editingSpouse : maritalStatus && SHOWS_SPOUSE.has(maritalStatus)) && (
        <SummaryRow label="Spouse">
          {isEditing && form ? (
            <PeopleSelector
              mode="single"
              value={form.spouseId}
              onChange={(spouseId) => update({ spouseId })}
              filter={takenIds(null)}
            />
          ) : (
            contactFullName(spouse)
          )}
        </SummaryRow>
      )}
      <SummaryRow label="Prior marriages">
        {isEditing && form ? (
          <div className="flex flex-col gap-4">
            <Select
              value={form.priorMarriages}
              onValueChange={(value) =>
                update({
                  priorMarriages: value as PriorMarriages,
                  marriages:
                    value === "yes" && form.marriages.length === 0
                      ? [createEmptyMarriage()]
                      : form.marriages,
                })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
            {form.priorMarriages === "yes" && (
              <EarlierMarriagesFields
                marriages={form.marriages}
                onChange={(index, field, value) =>
                  update({
                    marriages: form.marriages.map((m, i) =>
                      i === index ? { ...m, [field]: value } : m,
                    ),
                  })
                }
                onAdd={() => update({ marriages: [...form.marriages, createEmptyMarriage()] })}
                errors={errors}
              />
            )}
          </div>
        ) : (
          priorMarriages && (priorMarriages === "yes" ? "Yes" : "No")
        )}
      </SummaryRow>
      <SummaryRow label="Children">
        {isEditing && form ? (
          <div className="flex flex-col gap-4">
            {form.childIds.map((childId, index) => (
              <div key={`${childId ?? "new"}-${index}`} className="flex items-center gap-3">
                <PeopleSelector
                  mode="single"
                  className="flex-1"
                  value={childId}
                  onChange={(id) =>
                    update({ childIds: form.childIds.map((c, i) => (i === index ? id : c)) })
                  }
                  filter={(contact) => contact.id !== form.spouseId && takenIds(childId)(contact)}
                />
                <IconButton
                  label="Remove child"
                  onClick={() => update({ childIds: form.childIds.filter((_, i) => i !== index) })}
                >
                  <Trash2 />
                </IconButton>
              </div>
            ))}
            <button
              type="button"
              onClick={() => update({ childIds: [...form.childIds, null] })}
              className="flex items-center gap-1 self-start text-sm font-medium text-brand"
            >
              Add person
              <Plus className="size-4" />
            </button>
          </div>
        ) : (
          children.length > 0 && (
            <div className="flex flex-col">
              {children.map((child) => (
                <span key={child.id}>{contactFullName(child)}</span>
              ))}
            </div>
          )
        )}
      </SummaryRow>
    </SummaryCard>
  );
};
